// Enable hot reload
// import '@expo/metro-runtime' // Not working

import 'react-native-get-random-values'
import 'react-native-gesture-handler'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { InteractionManager, Platform, View, Text } from 'react-native'
import { Stack, SplashScreen } from 'expo-router'
import { ErrorBoundary } from '../app/screens/ErrorBoundary/ErrorBoundary'

import { StatusBar } from 'expo-status-bar'
// import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated'

import { enableFreeze, enableScreens } from 'react-native-screens'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { initialWindowMetrics, SafeAreaProvider } from 'react-native-safe-area-context'
import { PaperProvider, MD3LightTheme } from 'react-native-paper'
import { GoogleOAuthProvider } from '@react-oauth/google'

import { extendLogger, LoggerContexts } from '../app/utils/logger'
import { initFonts } from '../../assets/fonts'

import { initSegment, SegmentAnalyticsProvider } from '@/models/analytics/segment'
import { getEnvStage, StoreProvider } from '@/models'

import { useInitRemoteConfig } from '../module/remote-config/useInitRemoteConfig'
import { useRemoteConfig } from '../module/remote-config/useRemoteConfig'
import { createExpoUpdateBackgroundTask, useExpoUpdateStatus } from '@/hooks/useExpoUpdate'
import { DatadogProvider, getDataDogConfig, onDataDogSDKInitialized } from '@/modules/datadog'
import { AppVersionContextProvider } from '../app/utils/checkStoreVersion/checkAppVersionThenPromptToUpdate'
import { ActualHeightProvider } from '../app/hooks/use-actual-height'
import { GestureHandlerRootView } from 'react-native-gesture-handler'

import { AgoraProvider } from '@/modules/agora'
import { getPushAnonymousId, initializePush } from '@/modules/push'
import { useSilentSignIn } from '@/hooks/useSilentSignIn'
import { observer } from 'mobx-react-lite'
import Toast from 'react-native-toast-message'
import { toastConfig } from '@/design-system/toastConfig'
import { NetworkDownToast } from '../app/navigation/toast'
import { checkRedirect } from '../app/utils/redirect-detection'

const isWeb = Platform.OS === 'web'

// INFO: reanimated fix
if (isWeb) {
  global.__RUNTIME_KIND = 1
  // @ts-expect-error
  global._log = console.log
  // @ts-expect-error
  global._getAnimationTimestamp = () => performance.now()
  // @ts-expect-error
  global.__reanimatedLoggerConfig = {
    level: 'warn',
    strict: false
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // refetchOnWindowFocus: true
    }
  }
})

createExpoUpdateBackgroundTask()

// This is the default configuration
// configureReanimatedLogger({
//   level: ReanimatedLogLevel.warn,
//   strict: false // Reanimated runs in strict mode by default
// })

SplashScreen.preventAutoHideAsync()

enableScreens(true)
enableFreeze(false)

//
const logger = extendLogger(LoggerContexts.AppBase)

// Firebase web

function RootLayout() {
  // const isNavigationStateRestored = true
  const [env, setEnv] = useState<Awaited<ReturnType<typeof getEnvStage>>['stage'] | undefined>()

  useInitRemoteConfig()

  const expoUpdateState = useExpoUpdateStatus()

  const isHydrated = useMemo(() => {
    if (!expoUpdateState.hydrated || expoUpdateState.isPending) {
      return false
    }
    return !!env
  }, [env, expoUpdateState])
  // const isHydrated = useMemo(() => {
  //   if (!expoUpdateState.hydrated || expoUpdateState.isPending) {
  //     return false
  //   }
  //   return isNavigationStateRestored
  // }, [isNavigationStateRestored, expoUpdateState])

  // Kick off initial async loading actions, like loading fonts and RootStore
  useEffect(() => {
    const bootstrapApp = async () => {
      try {
        logger.info('bootstrapApp')
        await initFonts()
        // Brings up the push provider (Customer.io). No-op on web. See src/module/push.
        await initializePush()
        // Then Segment, using Customer.io's anonymous ID (null on web, or if Customer.io
        // isn't running) so both report the same ID.
        await initSegment({ cioAnonymousId: await getPushAnonymousId() })

        const { stage } = await getEnvStage()
        stage && setEnv(stage)

        // DEBUG ONLY: Test crashes
        // Sentry.instance.nativeCrash()
        logger.info('bootstrapApp completed')
      } catch (error) {
        logger.error('useEffect', error)
      }
    }
    bootstrapApp()
  }, [])

  useEffect(() => {
    if (isHydrated) {
      const interaction = InteractionManager.runAfterInteractions(() => {
        SplashScreen.hide()
      })
      return () => {
        interaction.cancel()
      }
    }
  }, [isHydrated])

  useEffect(() => {
    checkRedirect()
  }, [])

  if (!isHydrated) {
    return null
  }

  return (
    <>
      <Providers env={env}>
        <Stack
          screenOptions={{
            headerShown: false
          }}
        >
          <Stack.Screen
            name="(auth)/verify-email/[code]"
            options={{
              presentation: 'transparentModal',
              animation: 'none',
              gestureEnabled: true,
              headerShown: false
            }}
          />

          <Stack.Screen
            name="login"
            options={{
              headerShown: false,
              animation: 'fade'
            }}
          />

          <Stack.Screen
            name="(auth)/reset-password/[code]"
            options={{
              presentation: 'transparentModal',
              animation: 'none',
              gestureEnabled: true,
              headerShown: false
            }}
          />
        </Stack>
      </Providers>
      <StatusBar style="auto" />
    </>
  )
}

const WebProviders = observer(({ children }: PropsWithChildren) => {
  // Normal web Auth0 session restore; only the Bettermode-specific blocking loader was removed.
  useSilentSignIn()

  return (
    <GoogleOAuthProvider clientId="469309110195-rf5ngun6lnkf7lqni9ao65t2imtig1d3.apps.googleusercontent.com">
      <AgoraProvider>{children}</AgoraProvider>
    </GoogleOAuthProvider>
  )
})

function NativeProviders({
  children,
  env
}: PropsWithChildren<{ env?: Awaited<ReturnType<typeof getEnvStage>>['stage'] }>) {
  return (
    <DatadogProvider configuration={getDataDogConfig(env)} onInitialization={onDataDogSDKInitialized}>
      {children}
    </DatadogProvider>
  )
}

function Providers({ children, env }: PropsWithChildren<{ env?: Awaited<ReturnType<typeof getEnvStage>>['stage'] }>) {
  const PlatformProvider = isWeb ? WebProviders : NativeProviders
  const { text1, text2 } = useRemoteConfig((state) => state.maintenance_msg)

  const showToast = useCallback(() => {
    if (!text1) return
    Toast.show({
      type: 'ppToast',
      text1: text1,
      text2: text2
    })
  }, [text1, text2])

  useEffect(() => {
    showToast()
  }, [showToast])

  return (
    <StoreProvider>
      <QueryClientProvider client={queryClient}>
        <PaperProvider theme={MD3LightTheme}>
          <AppVersionContextProvider>
            <ActualHeightProvider>
              <GestureHandlerRootView style={{ flex: 1 }}>
                <SafeAreaProvider initialMetrics={initialWindowMetrics}>
                  <SegmentAnalyticsProvider>
                    {/* <RootNavigator initialState={initialNavigationState} onStateChange={onNavigationStateChange} /> */}
                    <PlatformProvider env={env}>{children}</PlatformProvider>
                    <Toast config={toastConfig} />
                    <NetworkDownToast />
                  </SegmentAnalyticsProvider>
                </SafeAreaProvider>
              </GestureHandlerRootView>
            </ActualHeightProvider>
          </AppVersionContextProvider>
        </PaperProvider>
      </QueryClientProvider>
    </StoreProvider>
  )
}

export { ErrorBoundary }

export default RootLayout
